import { Autocomplete, Box, Button, TextField } from '@mui/material';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBulkContentTag } from '~/api/content-tags';
import { Dialog, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { assert } from '~/lib/assert';

export interface ContentBulkTagDialogProps {
  contentIds: string[];
  contentTags: string[];
  onClose: () => void;
  onComplete: () => void;
  open: boolean;
}

export const ContentBulkTagDialog = ({
  contentIds,
  contentTags,
  onClose,
  onComplete,
  open,
}: ContentBulkTagDialogProps) => {
  const { t } = useTranslation();
  const [selectedTag, setSelectedTag] = useState<string | null>(null);

  const [bulkTag, { called }] = useBulkContentTag();

  const handleClose = useCallback(() => {
    setSelectedTag(() => null);
    onClose();
  }, [onClose]);

  return (
    <Dialog maxWidth="xs" fullWidth onClose={handleClose} open={open}>
      <DialogTitle onClose={handleClose}>{t('pages.content.dialogs.add_tag.title')}</DialogTitle>

      <DialogContent>
        <Box display="flex" alignItems="center" justifyContent="center" padding={2}>
          <Autocomplete
            freeSolo
            fullWidth
            onChange={(_event, option) => setSelectedTag(option ?? null)}
            onInputChange={(_, value) => setSelectedTag(value)}
            options={contentTags}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label={t('common.tags.choose_tag')}
                variant="outlined"
              />
            )}
            value={selectedTag || null}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" onClick={handleClose}>
          {t('common.dialogs.cancel')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={called || !selectedTag}
          onClick={async () => {
            assert(selectedTag != null, 'tag is null');
            await bulkTag({
              variables: {
                items: contentIds.map((x) => {
                  const parts = x.split('-');
                  return {
                    taggableId: Number(parts[1]),
                    taggableType: parts[0] === 'ContentItem' ? 'APP' : 'MEDIA',
                  };
                }),
                tag: selectedTag,
              },
            });
            handleClose();
            onComplete();
          }}
        >
          {t('common.dialogs.add')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
